import React from 'react';
import { useParams } from 'react-router-dom';
import OpenSsoApp from './OpenSsoApp';
import { resolveSsoBoot } from './resolveSsoBoot';
import { ssoErrorMessage } from './ssoErrorMessage';
import { SSO_EVENTS } from '../../constants/sso.constants';
import DataController from '../../data/data.controller';
import SsoIdentityBar from './elements/SsoIdentityBar';
import barStyles from './elements/styles/sso-identity-bar.module.scss';

// Full-viewport flex host for the RN tree. Rendering rn-ui directly into a div
// (rather than via AppRegistry.runApplication) leaves the RN root with no size,
// so every flex:1 collapses to height 0 and the screens pile up. This gives the
// tree a viewport-sized flex parent, which is what AppRegistry would have done.
const host = {
    position: 'fixed',
    inset: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#FFFFFF',
};

const fullScreen = {
    ...host,
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'sans-serif',
    color: '#64748B',
};

/**
 * Route target for Open SSO. Resolves the FI's apiKey + branding, then renders
 * the embedded consumer app impersonating the selected user (by clientId).
 *
 * Runs behind ProtectedRoute — the admin's auth cookie carries over to this tab,
 * so no separate login is needed.
 *
 * Reports `session_closed` when the tab goes away, so an SSO session has an end
 * in the audit log and not just a beginning. It fires on pagehide rather than
 * beforeunload because beforeunload never fires for a tab restored from the
 * back/forward cache on mobile Safari, and on the unmount path for an in-app
 * navigation away from the route.
 */
export default function OpenSsoView() {
    const { clientId } = useParams();
    const [boot, setBoot] = React.useState(null);
    const [error, setError] = React.useState(null);
    const [exited, setExited] = React.useState(false);
    // Shared by the Exit button and the unload listeners: a session ends once,
    // and clicking Exit fires pagehide immediately after via window.close().
    const closeReported = React.useRef(false);

    React.useEffect(() => {
        let cancelled = false;

        (async () => {
            try {
                const resolved = await resolveSsoBoot(clientId);
                if (cancelled) return;
                setBoot(resolved);
            } catch (err) {
                console.error('Open SSO boot failed:', err);
                if (cancelled) return;
                setError(ssoErrorMessage(err));
            }
        })();

        return () => { cancelled = true; };
    }, [clientId]);

    /**
     * Report that this session ended, at most once.
     * @param {boolean} unloading True when the page is going away, which needs
     *   the keepalive transport — a normal request would be cancelled.
     */
    const reportSessionClosed = React.useCallback((unloading) => {
        if (closeReported.current || !boot?.sessionId) return;
        closeReported.current = true;

        const data = DataController.Instance();
        const aba = boot.institution?.aba;
        if (unloading) data.reportSsoEventOnUnload(aba, clientId, SSO_EVENTS.SESSION_CLOSED, boot.sessionId);
        else data.reportSsoEvent(aba, clientId, SSO_EVENTS.SESSION_CLOSED, boot.sessionId);
    }, [boot, clientId]);

    /**
     * Leave the member's account. Reports the close, tears the embed down, then
     * asks the browser to close the tab.
     *
     * The teardown is not conditional on the tab actually closing: window.close()
     * is only honoured for a script-opened window, and this route can also be
     * reached by pasting the URL. Rendering the ended state regardless means the
     * admin is visibly out of the account either way.
     */
    const handleExit = React.useCallback(() => {
        reportSessionClosed(false);
        setExited(true);
        window.close();
    }, [reportSessionClosed]);

    // Separate effect, keyed on the resolved session: it must not re-run when
    // the boot is still resolving, and it needs the sessionId the boot returned.
    React.useEffect(() => {
        if (!boot?.sessionId) return;

        const onUnload = () => reportSessionClosed(true);
        // Both, because neither alone is enough: beforeunload never fires for a
        // tab restored from the back/forward cache, and pagehide is skipped by
        // some unload paths. The ref makes the overlap free.
        window.addEventListener('pagehide', onUnload);
        window.addEventListener('beforeunload', onUnload);

        return () => {
            window.removeEventListener('pagehide', onUnload);
            window.removeEventListener('beforeunload', onUnload);
            // Navigating away in-app: a normal request still completes here.
            reportSessionClosed(false);
        };
    }, [boot, clientId, reportSessionClosed]);

    if (error) return <div style={fullScreen}>{error}</div>;
    if (exited) return <div style={fullScreen}>Session ended. You can close this tab.</div>;
    if (!boot) return <div style={fullScreen}>Loading…</div>;

    return (
        <div style={host}>
            <SsoIdentityBar identity={boot.identity} onExit={handleExit} />
            <div className={barStyles.hostContent}>
                <OpenSsoApp boot={boot} />
            </div>
        </div>
    );
}
